/**
 * Renders layers from control-preview without LiveKit.
 * Used for static previews (event-info, flow-info, session-info, media, etc.) in PreviewModal and PreviewGrid.
 * LiveKit layers show a placeholder; static and media layers render actual content.
 */

import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { VideocamOffOutlined, VideocamOutlined } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from 'react';
import { getBackgroundStyles, getBackgroundStylesFromLayerData } from '@/helpers/preview/background.helper';
import type { PlayerEventSettings } from '@/types/preview.types';
import { isPreviewAudioCapableLayer, PreviewAudioToggleButton } from '@/components/preview/preview-audio-toggle-button';
import { PreviewVideoPlayer } from '@/components/preview/preview-video-player';
import { StillFrameVideo } from '@/components/preview/still-frame-video';
import { SyncedFileVideo } from '@/components/preview/synced-file-video';
import { readPlaybackStartedAtMs } from '@/helpers/preview/synced-file-video.helper';
import { StillFrameImage } from '@/components/preview/still-frame-image';
import { useStillFrameReadyRegistration } from '@/components/preview/still-frame-ready-context';
import { PreviewScaledHtmlLayer } from '@/components/preview/preview-scaled-html-layer';
import { PreviewPdfLayer } from '@/components/preview/preview-pdf-layer';
import { PreviewPresentationSlidesLayer } from '@/components/preview/preview-presentation-slides-layer';
import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import { PreviewSponsorsLayer } from '@/components/preview/preview-sponsors-layer';
import { usePreviewStageMetrics } from '@/helpers/preview/preview-viewport-scale.helper';
import { resolvePreviewHtmlFontSizePx } from '@/helpers/preview/sanitize-preview-html.helper';
import { DEFAULT_FONT_SIZE_CQH, htmlFontResolvesAgainstStage } from '@/helpers/preview/font-size-cqh.helper';
import { getLayerCutClipPath } from '@/helpers/preview/layer-cut.helper';
import { useTranslations } from '@/i18n/use-translations';
import { useSimulationLocalCameraStore } from '@/state/stores/use-simulation-local-camera-store';

export interface PreviewLayer {
  layerId: number;
  componentId: string;
  position: { x: number; y: number; w: number; h: number };
  /** Horizontal crop (percent of layer width) applied via clip-path, mirroring the player. */
  cutLeft?: number | null;
  cutRight?: number | null;
  data: Record<string, string>;
  style?: {
    textColor?: string;
    fontSizeMultiplier?: number;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
    maxLines?: number;
  };
  visible?: boolean;
  preload?: boolean;
  trackSid?: string;
  itemId?: string;
  audible?: boolean;
}

export interface PreviewStaticLayerRendererProps {
  layers: PreviewLayer[];
  settings?: PlayerEventSettings | null;
  height?: string | number;
  /** When set, only this item's media fires playback-complete (simulation advance modes). */
  currentOnAirItemId?: string;
  onLayerMediaEnded?: (itemId: string) => void;
  shouldLoopLayer?: (layer: PreviewLayer) => boolean;
  /** Show play/pause + progress controls on video layers. */
  mediaControls?: boolean;
  /**
   * Control Now/Next output: show video first frame only (no autoplay, no controls).
   * Does not apply to MediaPreview / PreviewModal item previews.
   */
  stillFrame?: boolean;
  /** Live Current: file videos play at the players' position (layer `playbackStartedAt`). */
  syncFileVideo?: boolean;
  /** Tight slide preview (media modal): black letterbox backdrop matching playout. */
  presentationPreview?: boolean;
  slideNavigationScope?: PresentationSlideNavigationScope;
  sequencePlaybackControls?: boolean;
  /** Control Next: use absolute goto for live slide control (see PreviewPresentationSlidesLayer). */
  preferAbsoluteSlideGoto?: boolean;
  /** Controlled preview listen state. Ignored when stillFrame. */
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
  /** Show in-frame listen toggle. Default true when !stillFrame. */
  showInFrameAudioToggle?: boolean;
  /** Override baked inline HTML colors with settings.textColor (Customize live preview). */
  forceHtmlTextColor?: boolean;
}

const LIVEKIT_COMPONENT_IDS = new Set([
  'livekitTrackScreenshare',
  'livekitTrackMediaAgent',
  'livekitTrackRoomCam',
  'livekitTrackRemoteSpeaker',
  'livekitTrack',
  'livekitTrackGrid',
]);

/** Layer remount / src swap can fire `ended` near t=0 while the real item is still on-air. */
function isLikelySpuriousMediaEnded(element: HTMLMediaElement): boolean {
  if (!Number.isFinite(element.currentTime) || element.currentTime > 0.5) {
    return false;
  }
  if (!Number.isFinite(element.duration) || element.duration <= 0) {
    return true;
  }
  return element.duration - element.currentTime > 0.5;
}

function wrapMediaEndedHandler(onMediaEnded?: () => void): React.ReactEventHandler<HTMLMediaElement> | undefined {
  if (!onMediaEnded) {
    return undefined;
  }
  return (event) => {
    if (isLikelySpuriousMediaEnded(event.currentTarget)) {
      return;
    }
    onMediaEnded();
  };
}

export function PreviewStaticLayerRenderer({
  layers,
  settings,
  height = '100%',
  currentOnAirItemId,
  onLayerMediaEnded,
  shouldLoopLayer,
  mediaControls = false,
  stillFrame = false,
  syncFileVideo = false,
  presentationPreview = false,
  slideNavigationScope = 'local',
  sequencePlaybackControls,
  preferAbsoluteSlideGoto = false,
  previewAudioEnabled: previewAudioEnabledProp,
  onPreviewAudioEnabledChange,
  showInFrameAudioToggle,
  forceHtmlTextColor = false,
}: PreviewStaticLayerRendererProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const { scale: stageViewportScale, stageHeightPx } = usePreviewStageMetrics(frameRef);
  const [internalAudioEnabled, setInternalAudioEnabled] = useState(false);
  const audioEnabled = stillFrame ? false : (previewAudioEnabledProp ?? internalAudioEnabled);
  const setAudioEnabled = (enabled: boolean) => {
    if (stillFrame) return;
    onPreviewAudioEnabledChange?.(enabled);
    if (previewAudioEnabledProp === undefined) {
      setInternalAudioEnabled(enabled);
    }
  };
  const sortedLayers = useMemo(() => [...layers].sort((a, b) => a.layerId - b.layerId), [layers]);
  const agentDeckVideoOnAir = sortedLayers.some(
    (layer) =>
      layer.componentId === 'livekitTrackMediaAgent' &&
      layer.visible !== false &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('mediaAgent:'),
  );
  const showAudioToggle = useMemo(
    () =>
      !stillFrame &&
      (showInFrameAudioToggle ?? true) &&
      sortedLayers.some((layer) => layer.visible !== false && isPreviewAudioCapableLayer(layer)),
    [sortedLayers, stillFrame, showInFrameAudioToggle],
  );
  const frameBackgroundSx = presentationPreview
    ? { bgcolor: '#000' as const }
    : settings
      ? getBackgroundStyles(settings)
      : { bgcolor: '#000' as const };

  return (
    <Box
      ref={frameRef}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        containerType: 'size',
        ...frameBackgroundSx,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          bgcolor: '#000',
          pointerEvents: 'none',
        }}
      />
      {sortedLayers.map((layer) => {
        const visible = layer.visible !== false;
        const isPreload = layer.preload === true;
        const shouldRender = visible || isPreload;
        const shouldShow = visible && !isPreload;

        if (!shouldRender || !layer.position) return null;

        const cutClipPath = getLayerCutClipPath(layer.cutLeft, layer.cutRight);
        const containerSx = {
          position: 'absolute' as const,
          left: `${layer.position.x}%`,
          top: `${layer.position.y}%`,
          width: `${layer.position.w}%`,
          height: `${layer.position.h}%`,
          overflow: 'hidden' as const,
          ...(cutClipPath ? { clipPath: cutClipPath, WebkitClipPath: cutClipPath } : {}),
          visibility: shouldShow ? ('visible' as const) : ('hidden' as const),
          opacity: shouldShow ? 1 : 0,
          pointerEvents: shouldShow ? ('auto' as const) : ('none' as const),
          zIndex: layer.layerId,
          // Match player: HTML scales with the layer box. Captions and info overlays use the stage.
          ...(htmlFontResolvesAgainstStage(layer) ? {} : { containerType: 'size' as const }),
        };

        const key = `layer-${layer.layerId}`;

        if (LIVEKIT_COMPONENT_IDS.has(layer.componentId)) {
          return (
            <Box key={key} sx={containerSx}>
              <LiveKitLayerPlaceholder layer={layer} stillFrame={stillFrame} />
            </Box>
          );
        }

        return (
          <Box
            key={key}
            sx={{
              ...containerSx,
              ...(layer.componentId === 'html' ? {} : { color: settings?.textColor ?? '#fff' }),
            }}
          >
            <PreviewStaticLayerContent
              layer={layer}
              settings={settings}
              previewAudioEnabled={audioEnabled}
              loopMedia={shouldLoopLayer?.(layer)}
              stageViewportScale={stageViewportScale}
              stageHeightPx={stageHeightPx}
              slideNavigationScope={slideNavigationScope}
              sequencePlaybackControls={sequencePlaybackControls}
              preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
              listenForSlideKeys={
                shouldShow &&
                !(
                  agentDeckVideoOnAir &&
                  (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides')
                )
              }
              forceHtmlTextColor={forceHtmlTextColor}
              onMediaEnded={
                layer.itemId && layer.itemId === currentOnAirItemId && onLayerMediaEnded
                  ? () => onLayerMediaEnded(layer.itemId!)
                  : undefined
              }
              videoControls={!stillFrame && !syncFileVideo && mediaControls}
              stillFrame={stillFrame}
              syncFileVideo={syncFileVideo}
              onAir={shouldShow}
            />
          </Box>
        );
      })}
      {showAudioToggle && (
        <PreviewAudioToggleButton audioEnabled={audioEnabled} onToggle={() => setAudioEnabled(!audioEnabled)} />
      )}
    </Box>
  );
}

function isRoomCamPlaceholderComponent(componentId: string): boolean {
  return componentId === 'livekitTrackRoomCam' || componentId === 'livekitTrackRoomCamGrid';
}

function resolveSimulationLiveKitPlaceholderContent(
  componentId: string,
  t: (key: string, values?: { defaultValue?: string }) => string,
): ReactNode {
  if (isRoomCamPlaceholderComponent(componentId)) {
    return (
      <VideocamOutlined
        aria-label={t('flows.detail.control.simulation.placeholderRoomCam', {
          defaultValue: 'Room camera (simulated)',
        })}
        sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 'clamp(1.5rem, 28%, 3rem)' }}
      />
    );
  }
  if (componentId === 'livekitTrackRemoteSpeaker') {
    return t('flows.detail.control.simulation.placeholderRemote', { defaultValue: 'Remote speaker (simulated)' });
  }
  if (componentId === 'livekitTrackMediaAgent') {
    return t('flows.detail.control.simulation.placeholderAgent', { defaultValue: 'Media agent (simulated)' });
  }
  return t('flows.detail.control.simulation.placeholderLiveKit', { defaultValue: 'Live track (simulated)' });
}

/** Simulation Broadcast room-cam: shared local webcam (On air + Next; survives show-mode remounts). */
function SimulationRoomCamLocalPreview({ layer, stillFrame }: { layer: PreviewLayer; stillFrame: boolean }) {
  const t = useTranslations();
  const settleReady = useStillFrameReadyRegistration(stillFrame);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stream = useSimulationLocalCameraStore((s) => s.stream);
  const starting = useSimulationLocalCameraStore((s) => s.starting);
  const denied = useSimulationLocalCameraStore((s) => s.denied);
  const start = useSimulationLocalCameraStore((s) => s.start);
  const stop = useSimulationLocalCameraStore((s) => s.stop);

  useEffect(() => {
    settleReady?.();
  }, [settleReady, layer.layerId, layer.componentId, layer.itemId]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && stream) {
      video.srcObject = stream;
      void video.play().catch(() => {
        // Autoplay can fail if the tab is backgrounded; user already clicked to start.
      });
    }
    return () => {
      if (video) {
        video.srcObject = null;
      }
    };
  }, [stream]);

  const handleClick = useCallback(() => {
    if (starting) {
      return;
    }
    if (stream) {
      stop();
      return;
    }
    void start();
  }, [starting, stream, start, stop]);

  const activateLabel = t('flows.detail.control.simulation.enableLocalCamera', {
    defaultValue: 'Activate\ncamera',
  });
  const activateTooltip = activateLabel.replace(/\n/g, ' ');
  const deniedLabel = t('flows.detail.control.simulation.localCameraDenied', {
    defaultValue: 'Access\ndenied',
  });
  const deniedTooltip = deniedLabel.replace(/\n/g, ' ');
  const tooltip = stream
    ? t('flows.detail.control.simulation.disableLocalCamera', { defaultValue: 'Click to stop your camera' })
    : denied
      ? deniedTooltip
      : activateTooltip;

  return (
    <Tooltip title={tooltip}>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={Boolean(stream)}
        aria-label={tooltip}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
          }
        }}
        sx={{
          'width': '100%',
          'height': '100%',
          'display': 'flex',
          'flexDirection': 'column',
          'alignItems': 'center',
          'justifyContent': 'center',
          'gap': 0.5,
          'bgcolor': '#000',
          'cursor': starting ? 'wait' : 'pointer',
          'position': 'relative',
          'overflow': 'hidden',
          'outline': 'none',
          'px': 0.5,
          '&:focus-visible': { boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.55)' },
        }}
      >
        {stream ? (
          <>
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transform: 'scaleX(-1)',
              }}
            />
            <VideocamOffOutlined
              sx={{
                position: 'absolute',
                right: 6,
                bottom: 6,
                color: 'rgba(255,255,255,0.85)',
                fontSize: 18,
                pointerEvents: 'none',
              }}
            />
          </>
        ) : starting ? (
          <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.8)' }} />
        ) : (
          <>
            <VideocamOutlined
              sx={{
                color: denied ? 'error.light' : 'rgba(255,255,255,0.75)',
                fontSize: 'clamp(1.25rem, 22%, 2.5rem)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: denied ? 'error.light' : 'rgba(255,255,255,0.7)',
                textAlign: 'center',
                lineHeight: 1.15,
                fontSize: 'clamp(0.55rem, 8%, 0.75rem)',
                px: 0.25,
                whiteSpace: 'pre-line',
              }}
            >
              {denied ? deniedLabel : activateLabel}
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
}

function LiveKitLayerPlaceholder({ layer, stillFrame = false }: { layer: PreviewLayer; stillFrame?: boolean }) {
  const t = useTranslations();
  const settleReady = useStillFrameReadyRegistration(stillFrame);
  const content = resolveSimulationLiveKitPlaceholderContent(layer.componentId, t);
  const isIcon = typeof content !== 'string';

  useEffect(() => {
    // Simulation has no LiveKit room — placeholder is immediately "painted".
    settleReady?.();
  }, [settleReady, layer.layerId, layer.componentId, layer.itemId]);

  // On air + Next: shared local webcam for room-cam placeholders.
  if (isRoomCamPlaceholderComponent(layer.componentId)) {
    return <SimulationRoomCamLocalPreview layer={layer} stillFrame={stillFrame} />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0,0,0,0.5)',
      }}
    >
      {isIcon ? (
        content
      ) : (
        <Typography variant="caption" color="rgba(255,255,255,0.7)">
          {content}
        </Typography>
      )}
    </Box>
  );
}

/** Shared static/media layer body — used by control preview, simulation output, and LiveKit output fallback. */
export function PreviewStaticLayerContent({
  layer,
  settings,
  previewAudioEnabled = false,
  loopMedia = false,
  onMediaEnded,
  videoControls = false,
  stillFrame = false,
  syncFileVideo = false,
  stageViewportScale = 1,
  stageHeightPx = 0,
  slideNavigationScope = 'local',
  sequencePlaybackControls,
  preferAbsoluteSlideGoto = false,
  listenForSlideKeys = true,
  forceHtmlTextColor = false,
  onAir = false,
}: {
  layer: PreviewLayer;
  settings?: PlayerEventSettings | null;
  previewAudioEnabled?: boolean;
  loopMedia?: boolean;
  onMediaEnded?: () => void;
  videoControls?: boolean;
  stillFrame?: boolean;
  syncFileVideo?: boolean;
  stageViewportScale?: number;
  /** Full preview frame height — used to derive layer box height for HTML cqh. */
  stageHeightPx?: number;
  slideNavigationScope?: PresentationSlideNavigationScope;
  sequencePlaybackControls?: boolean;
  preferAbsoluteSlideGoto?: boolean;
  listenForSlideKeys?: boolean;
  forceHtmlTextColor?: boolean;
  /** Layer is visible and not preloading. */
  onAir?: boolean;
}) {
  const { componentId, data } = layer;
  // Prefer live settings so editors (Customize sticky preview) can override baked layer style.
  const textColor = settings?.textColor ?? layer.style?.textColor ?? '#fff';
  const playAudio = !stillFrame && previewAudioEnabled && layer.audible !== false;
  const handleMediaEnded = wrapMediaEndedHandler(stillFrame ? undefined : onMediaEnded);

  if (componentId === 'background') {
    // Prefer live settings when backgroundType is present so local editors
    // (e.g. Customize sticky preview) can update without rebuilding baked layer data.
    if (settings?.backgroundType) {
      const liveSettings = {
        ...settings,
        backgroundColor: settings.backgroundColor || data.backgroundColor,
        backgroundImageUrl: settings.backgroundImageUrl || data.imageUrl,
      };
      return <Box sx={{ width: '100%', height: '100%', ...getBackgroundStyles(liveSettings) }} />;
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          ...getBackgroundStylesFromLayerData({
            ...data,
            backgroundColor: data.backgroundColor || settings?.backgroundColor || '#000',
          }),
        }}
      />
    );
  }

  if (componentId === 'header' || componentId === 'text') {
    const content = data.title || data.content || componentId;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          bgcolor: 'rgba(0,0,0,0.3)',
        }}
      >
        <Typography variant="body2" color={textColor} sx={{ wordBreak: 'break-word' }}>
          {content}
        </Typography>
      </Box>
    );
  }

  if (componentId === 'video' || componentId === 'backgroundVideo') {
    const url = data.videoUrl || data.url;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
          overflow: 'hidden',
        }}
      >
        {url ? (
          stillFrame ? (
            <StillFrameVideo url={url} />
          ) : syncFileVideo ? (
            <SyncedFileVideo
              url={url}
              startedAtMs={readPlaybackStartedAtMs(data.playbackStartedAt)}
              loop={loopMedia || data.loop === 'true' || data.loop === '1'}
              muted={!playAudio || !onAir}
            />
          ) : videoControls ? (
            <PreviewVideoPlayer
              url={url}
              muted={!playAudio}
              loop={loopMedia}
              onEnded={loopMedia ? undefined : onMediaEnded}
            />
          ) : (
            <video
              key={url}
              autoPlay
              muted={!playAudio}
              playsInline
              loop={loopMedia}
              onEnded={loopMedia ? undefined : handleMediaEnded}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
              src={url}
            />
          )
        ) : (
          <Typography variant="body2" color="text.secondary">
            No video URL
          </Typography>
        )}
      </Box>
    );
  }

  if (componentId === 'audio') {
    const url = data.audioUrl || data.url;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
        }}
      >
        {url ? (
          <audio
            autoPlay={playAudio}
            muted={!playAudio}
            loop={loopMedia}
            onEnded={loopMedia ? undefined : handleMediaEnded}
            style={{ width: '80%', maxWidth: '600px' }}
            src={url}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No audio URL
          </Typography>
        )}
      </Box>
    );
  }

  if (componentId === 'image') {
    const url = data.src || data.imageUrl || data.url;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
          overflow: 'hidden',
        }}
      >
        {url ? (
          stillFrame ? (
            <StillFrameImage url={url} alt={data.alt || 'Image'} />
          ) : (
            <img
              key={url}
              src={url}
              alt={data.alt || 'Image'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image URL
          </Typography>
        )}
      </Box>
    );
  }

  if (componentId === 'pdf') {
    const url = data.pdfUrl || data.url;
    const layerCurrentIndex = data.currentIndex ? Number.parseInt(data.currentIndex, 10) : undefined;
    return url ? (
      <PreviewPdfLayer
        url={url}
        itemId={layer.itemId}
        slideNavigationScope={slideNavigationScope}
        currentIndex={layerCurrentIndex}
      />
    ) : (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No PDF URL
        </Typography>
      </Box>
    );
  }

  if (componentId === 'presentationSlides' || componentId === 'imageSequence') {
    return (
      <PreviewPresentationSlidesLayer
        data={data}
        itemId={layer.itemId}
        componentId={componentId}
        slideNavigationScope={slideNavigationScope}
        sequencePlaybackControls={sequencePlaybackControls}
        preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
        listenForSlideKeys={listenForSlideKeys}
        onMediaEnded={onMediaEnded}
        playVideoOverlays={!stillFrame && (slideNavigationScope !== 'live' || onAir)}
        previewAudioEnabled={playAudio}
      />
    );
  }

  if (componentId === 'html') {
    const heightPercent =
      typeof layer.position?.h === 'number' && Number.isFinite(layer.position.h) ? layer.position.h : 100;
    const layerHeightPx =
      stageHeightPx > 0 ? stageHeightPx * (htmlFontResolvesAgainstStage(layer) ? 1 : heightPercent / 100) : 0;
    return (
      <PreviewScaledHtmlLayer
        html={data.html ?? ''}
        textColor={settings?.textColor ?? layer.style?.textColor}
        fontSizeMultiplier={settings?.fontSizeMultiplier ?? layer.style?.fontSizeMultiplier}
        stageViewportScale={stageViewportScale}
        layerHeightPx={layerHeightPx}
        forceTextColor={forceHtmlTextColor}
      />
    );
  }

  if (componentId === 'sponsors') {
    return <PreviewSponsorsLayer sponsorsJson={data.sponsors} />;
  }

  if (componentId === 'liveTranslation') {
    const captionColor = settings?.liveTranslationTextColor ?? layer.style?.textColor ?? '#fff';
    const fontCqh =
      settings?.liveTranslationFontSizeMultiplier ?? layer.style?.fontSizeMultiplier ?? DEFAULT_FONT_SIZE_CQH;
    const fontSizePx = resolvePreviewHtmlFontSizePx(stageHeightPx, fontCqh);
    const maxLines = Math.max(1, layer.style?.maxLines ?? 2);
    const lineHeight = 1.25;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          overflow: 'hidden',
          boxSizing: 'border-box',
          px: 1,
          pt: '0.32em',
          pb: '0.35em',
          color: captionColor,
          backgroundColor: (layer.style?.backgroundColor as string | undefined) ?? 'rgba(0,0,0,0.6)',
          fontSize: fontSizePx > 0 ? `${fontSizePx}px` : undefined,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxHeight: `calc(${maxLines} * ${lineHeight}em)`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            sx={{
              fontSize: 'inherit',
              lineHeight,
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              wordBreak: 'normal',
            }}
          >
            EN Hello, and welcome to the session.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: settings?.backgroundColor || data.backgroundColor || '#000',
      }}
    >
      <Typography variant="caption" color="rgba(255,255,255,0.6)">
        {componentId}
      </Typography>
    </Box>
  );
}
